// i18n — IKKI QATLAMNING BIRINCHISI (07-FRONTEND.md §5).
//
// Bu yerda faqat BIZNING matnlarimiz tarjima qilinadi. antd komponentlarining
// ichki matnlari ("Ma'lumot yo'q", "Bekor qilish", sana formati) alohida
// qatlam — `antdLocales.ts` ga qarang. Ikkinchisini unutish o'zbekcha panelda
// jadval tagida inglizcha "No data" chiqishiga olib keladi.

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './en.json'
import fa from './fa.json'
import ru from './ru.json'
import uz from './uz.json'

export const SUPPORTED_LANGUAGES = ['en', 'ru', 'uz', 'fa'] as const
export type Language = (typeof SUPPORTED_LANGUAGES)[number]

export const DEFAULT_LANGUAGE: Language = 'en'

const STORAGE_KEY = 'xsib.lang'

/** Intl uchun to'liq locale kodlari — sana, raqam va bayt formatlash shu bo'yicha */
export const INTL_LOCALES: Record<Language, string> = {
  en: 'en-US',
  ru: 'ru-RU',
  uz: 'uz-UZ',
  // ⚠️ Kengaytmalarsiz `fa-AF` forscha raqam (۱۲۳) va quyosh hijriy
  // taqvimini beradi — ikkalasi ham bu yerda kerak emas.
  fa: 'fa-AF-u-ca-gregory-nu-latn',
}

function isLanguage(value: string): value is Language {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

function readStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && isLanguage(stored)) return stored
  } catch {
    // localStorage mavjud bo'lmasligi mumkin (private rejim, sozlama)
  }
  return DEFAULT_LANGUAGE
}

export function storeLanguage(lang: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    // e'tiborsiz qoldiramiz — til tanlovi saqlanmasligi panelni to'xtatmaydi
  }
}

/** Joriy tilni `Language` turiga keltiradi */
export function currentLanguage(): Language {
  const lang = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LANGUAGE
  const base = lang.split('-')[0] ?? DEFAULT_LANGUAGE
  return isLanguage(base) ? base : DEFAULT_LANGUAGE
}

// Ro'yxat ataylab — keyin pushtu ('ps') qo'shilsa bitta satr yetadi.
export const RTL_LANGUAGES: readonly Language[] = ['fa']

export function isRtl(lang: Language = currentLanguage()): boolean {
  return RTL_LANGUAGES.includes(lang)
}

// antd `ConfigProvider direction` dan oladi, CSS va brauzer esa `<html dir>`
// dan — ikkalasi qo'yilmasa panel yarim o'girilgan qoladi.
function applyDirection(lang: Language): void {
  document.documentElement.dir = isRtl(lang) ? 'rtl' : 'ltr'
}

/** Joriy til uchun Intl locale kodi */
export function currentIntlLocale(): string {
  return INTL_LOCALES[currentLanguage()]
}

export function changeLanguage(lang: Language): void {
  storeLanguage(lang)
  void i18n.changeLanguage(lang)
  document.documentElement.lang = lang
  applyDirection(lang)
}

const initialLanguage = readStoredLanguage()

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fa: { translation: fa },
    ru: { translation: ru },
    uz: { translation: uz },
  },
  lng: initialLanguage,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  interpolation: {
    escapeValue: false, // React o'zi ekranlaydi
  },
  returnNull: false,
})

document.documentElement.lang = initialLanguage
applyDirection(initialLanguage)

export default i18n
